import mixpanel from 'mixpanel-browser'
import { analyticsApi, AnalyticsApiResponse } from '~/api/analytics'
import { AuthenticationError } from '~/common/fetchErrors'

/**
 * MIXPANEL_EVENT enum defines the event names used for Mixpanel tracking.
 */
export enum MIXPANEL_EVENT {
  PAGE_ERROR = 'Page Error',
  // Also emitted from templates/utilities/doc_viewer.html via window.trackMixpanel, which
  // cannot import this enum — keep the string in sync.
  DOC_VIEWER_LOAD_FAILED = 'Doc Viewer Load Failed'
}

// Extend Window interface for Mixpanel
declare global {
  interface Window {
    mixpanel?: typeof mixpanel & { __loaded?: boolean }
    trackMixpanelPageView?: () => void
    addMixpanelAnalyticsToPage?: () => void
    trackMixpanel?: (event_type: string, props: Record<string, string | number | null | undefined | object>) => void
    __mixpanelEnabled?: boolean // Cache for settings gate status
    __mixpanelLocalMode?: boolean // When true, console.log events instead of sending to Mixpanel
    __mixpanelErrorTrackingInitialized?: boolean // Prevent duplicate error listeners
    __mixpanelTrackedErrors?: Set<string> // Deduplication for errors
  }
}

// Convert a URL segment to Title Case (handles snake_case and kebab-case)
function formatSegmentToTitleCase(segment: string): string {
  return segment
    .replace(/[-_]/g, ' ') // Replace hyphens/underscores with spaces
    .replace(/\b\w/g, char => char.toUpperCase()) // Capitalize first letter of each word
}

// Dynamically generate event name from current URL using first two meaningful path segments
function getPageViewEventName(url: string): string {
  try {
    const { pathname } = new URL(url)
    const segments = pathname
      .split('/')
      .filter(Boolean)
      // Keep 1st segment always, filter out any segment after that if it contains numbers
      .filter((segment, index) => index === 0 || !/\d/.test(segment))
      // Take first 2 meaningful segments
      .slice(0, 2)

    if (segments.length === 0) {
      return 'Root Page Viewed'
    }

    // Convert each segment to Title Case
    const formattedSegments = segments.map(formatSegmentToTitleCase)

    return `${formattedSegments.join(' ')} Page Viewed`
  } catch {
    // Fallback for invalid URLs
    const segments = url
      .split('/')
      .filter(Boolean)
      // Keep 1st segment always, filter out any segment after that if it contains numbers
      .filter((segment, index) => index === 0 || !/\d/.test(segment))
      .slice(0, 2)

    if (segments.length === 0) {
      return 'Home Page Viewed'
    }

    const formattedSegments = segments.map(formatSegmentToTitleCase)
    return `${formattedSegments.join(' ')} Page Viewed`
  }
}

/**
 * Dynamically generate button event name from pathname and button ID.
 * Uses first 2 non-numeric URL segments.
 *
 * @example
 * getButtonEventName('new_sow', '/sow/163/settings/')
 * // Returns: "Sow Settings New Sow Clicked"
 *
 * @param buttonId - The button identifier (e.g., 'submit-timesheet', 'save_draft')
 * @param pathname - The URL pathname (e.g., '/invoices/123/edit')
 * @returns Formatted event name
 */
export function getButtonEventName(buttonId: string, pathname: string): string {
  // Handle undefined pathname (can happen in test environments)
  const safePath = pathname || '/'

  // Extract non-numeric segments from pathname
  const segments = safePath
    .split('/')
    .filter(Boolean) // Remove empty strings
    .filter(segment => !/\d/.test(segment)) // Skip segments with numbers
    .slice(0, 2) // Take first 2 meaningful segments

  // Format URL segments to Title Case
  const pageContext = segments.length > 0 ? segments.map(formatSegmentToTitleCase).join(' ') : 'Root'

  // Format button ID to Title Case
  const buttonAction = formatSegmentToTitleCase(buttonId)

  return `${pageContext} ${buttonAction} Clicked`
}

// ============================================================================
// URL SANITIZATION
// ============================================================================

/**
 * Redacts the 'search' query parameter value to avoid sending user input to Mixpanel.
 * Keeps all other query parameters intact so we can track feature usage (filters, pagination, etc.)
 *
 * @example
 * redactSearchParam('https://example.com/jobs?search=john&status=Active')
 * // Returns: 'https://example.com/jobs?search=REDACTED&status=Active'
 *
 * @param url - The full URL to sanitize
 * @returns URL with 'search' parameter value replaced with 'REDACTED'
 */
function redactSearchParam(url: string): string {
  try {
    const urlObj = new URL(url)
    const params = new URLSearchParams(urlObj.search)

    // Replace search param value with REDACTED if it exists
    if (params.has('search')) {
      params.set('search', '***')
    }

    // Reconstruct URL with modified query params
    urlObj.search = params.toString()
    return urlObj.toString()
  } catch {
    // Fallback: return original URL if parsing fails
    return url
  }
}

// ============================================================================
// TRACKING HIGHER-ORDER FUNCTIONS (Composition Pattern)
// ============================================================================

/**
 * Higher-order function that wraps a click handler with Mixpanel button tracking.
 * Uses composition over inheritance - no need to modify Button components.
 *
 * @example
 * <Button onClick={trackedButtonClick(handleSubmit, 'submit_form', { form_id: 123 })} />
 * <IconButton onClick={trackedButtonClick(handleDelete, 'delete_item')} />
 *
 * @param handler - The original click handler to call after tracking
 * @param mpName - Button identifier for tracking (e.g., 'submit_form', 'delete_item')
 * @param mpAdditionalProps - Optional additional properties to track
 * @returns A new click handler that tracks then calls the original handler
 */
export function trackedButtonClick<E = React.MouseEvent<HTMLElement, MouseEvent>>(
  handler: ((e: E) => void) | undefined,
  mpName: string,
  mpAdditionalProps?: Record<string, string | number | null | undefined | object>
): (e: E) => void {
  return (e: E) => {
    const pathname = window.location?.pathname || '/'
    const eventName = getButtonEventName(mpName, pathname)
    track(eventName, {
      event_type: 'button_click',
      button_id: mpName,
      ...mpAdditionalProps
    })
    handler?.(e)
  }
}

/**
 * Higher-order function for tracking link clicks.
 * Uses composition over inheritance - no need to modify Link components.
 *
 * @example
 * <Link href="/tasks" onClick={trackedLinkClick(undefined, 'go_to_tasks', { count: 5 })} />
 *
 * @param handler - The original click handler to call after tracking
 * @param mpName - Link identifier for tracking
 * @param mpAdditionalProps - Optional additional properties to track
 * @returns A new click handler that tracks then calls the original handler
 */
export function trackedLinkClick<E = React.MouseEvent<HTMLAnchorElement, MouseEvent>>(
  handler: ((e: E) => void) | undefined,
  mpName: string,
  mpAdditionalProps?: Record<string, string | number | null | undefined | object>
): (e: E) => void {
  return (e: E) => {
    const pathname = window.location?.pathname || '/'
    const eventName = getButtonEventName(mpName, pathname)
    track(eventName, {
      event_type: 'link_click',
      link_id: mpName,
      ...mpAdditionalProps
    })
    handler?.(e)
  }
}

/**
 * Higher-order function for tracking tab changes (FilterTabs, NavTab, PageTab).
 * Uses composition over inheritance - no need to modify Tab components.
 *
 * @example
 * <FilterTabs onChange={trackedTabChange(setView, 'filter_status', { count: 10 })} />
 * <NavTab onClick={trackedTabChange(handleTabClick, 'nav_overview')} />
 *
 * @param handler - The original change/click handler to call after tracking
 * @param mpName - Tab identifier for tracking
 * @param mpAdditionalProps - Optional additional properties to track
 * @returns A new handler that tracks then calls the original handler
 */
export function trackedTabChange<T = string>(
  handler: ((value: T) => void) | undefined,
  mpName: string,
  mpAdditionalProps?: Record<string, string | number | null | undefined | object>
): (value: T) => void {
  return (value: T) => {
    const pathname = window.location?.pathname || '/'
    const eventName = getButtonEventName(mpName, pathname)
    track(eventName, {
      event_type: 'tab_click',
      tab_name: typeof value === 'string' ? value : mpName,
      ...mpAdditionalProps
    })
    handler?.(value)
  }
}

/**
 * Higher-order function for tracking drawer/modal open actions.
 * Uses composition over inheritance - no need to modify Drawer components.
 *
 * @example
 * <Button onClick={trackedDrawerOpen(onOpen, 'tasks_drawer', { task_count: 5 })} />
 *
 * @param handler - The original open handler to call after tracking
 * @param drawerName - Drawer/modal identifier for tracking
 * @param mpAdditionalProps - Optional additional properties to track
 * @returns A new handler that tracks then calls the original handler
 */
export function trackedDrawerOpen(
  handler: (() => void) | undefined,
  drawerName: string,
  mpAdditionalProps?: Record<string, string | number | null | undefined | object>
): () => void {
  return () => {
    const pathname = window.location?.pathname || '/'
    const eventName = getButtonEventName(drawerName, pathname)
    track(eventName, {
      event_type: 'drawer_open',
      drawer_name: drawerName,
      ...mpAdditionalProps
    })
    handler?.()
  }
}

// Get page load time in milliseconds using Navigation Timing API
function getPageLoadTime(): number | null {
  if (performance.getEntriesByType) {
    const [navigation] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]
    if (navigation && navigation.domContentLoadedEventEnd) {
      return Math.round(navigation.domContentLoadedEventEnd)
    }
  }
  return null
}

// ============================================================================
// ERROR TRACKING
// ============================================================================

// Generate a unique key for error deduplication
function getErrorKey(errorType: string, message: string, source?: string, line?: number): string {
  return `${errorType}|${message}|${source || ''}|${line || ''}`
}

// Extensions inject into the page; their unhandled rejections / uncaught errors are not app bugs.
// Mixpanel Page Errors like Surfshark's "Key not found" and MetaMask's "Failed to connect" are
// always chrome-extension:// (or moz-/safari- equivalent) in filename or stack.
function isBrowserExtensionError(source?: string, stack?: string) {
  const haystack = `${source ?? ''}\n${stack ?? ''}`
  return (
    haystack.includes('chrome-extension://') ||
    haystack.includes('moz-extension://') ||
    haystack.includes('safari-extension://') ||
    haystack.includes('safari-web-extension://')
  )
}

// Track an error event to Mixpanel
function trackErrorToMixpanel(
  errorType: 'uncaught_error' | 'unhandled_rejection',
  message: string,
  source?: string,
  line?: number,
  column?: number,
  stack?: string
) {
  if (isBrowserExtensionError(source, stack)) {
    return
  }

  // Initialize deduplication set if needed
  if (!window.__mixpanelTrackedErrors) {
    window.__mixpanelTrackedErrors = new Set()
  }

  // Deduplicate errors (same error type + message + source + line within session)
  const errorKey = getErrorKey(errorType, message, source, line)
  if (window.__mixpanelTrackedErrors.has(errorKey)) {
    return // Skip duplicate error
  }
  window.__mixpanelTrackedErrors.add(errorKey)

  // Limit the set size to prevent memory leaks (keep last 100 errors)
  if (window.__mixpanelTrackedErrors.size > 100) {
    const firstKey = window.__mixpanelTrackedErrors.values().next().value
    if (firstKey) {
      window.__mixpanelTrackedErrors.delete(firstKey)
    }
  }

  track(MIXPANEL_EVENT.PAGE_ERROR, {
    event_type: 'page_error',
    error_type: errorType,
    error_subtype: null,
    error_message: message.substring(0, 500), // Truncate message
    error_source: source || null,
    error_line: line || null,
    error_column: column || null,
    error_stack: stack ? stack.substring(0, 1000) : null, // Truncate stack
    component_stack: null,
    trace_id: null
  })
}

/**
 * Initialize Mixpanel error tracking.
 * Sets up global error handlers for uncaught errors and unhandled promise rejections.
 * Safe to call multiple times - will only initialize once.
 */
export function initMixpanelErrorTracking() {
  // Prevent duplicate initialization
  if (window.__mixpanelErrorTrackingInitialized) {
    return
  }
  window.__mixpanelErrorTrackingInitialized = true

  // Track uncaught JavaScript errors
  window.addEventListener('error', (event: ErrorEvent) => {
    // Skip if it's a resource loading error (those have a target but no message)
    if (event.target && (event.target as HTMLElement).tagName) {
      return
    }

    trackErrorToMixpanel(
      'uncaught_error',
      event.message || 'Unknown error',
      event.filename,
      event.lineno,
      event.colno,
      event.error?.stack
    )
  })

  // Track unhandled promise rejections
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason

    // Next.js's Pages Router cancels the in-flight render when a new navigation starts
    // before the previous one commits, rejecting with an Error marked `cancelled: true`.
    // This is expected router behavior (e.g. fast repeat navigation), not an app bug.
    if (
      reason instanceof Error &&
      reason.message === 'Cancel rendering route' &&
      (reason as Error & { cancelled?: boolean }).cancelled
    ) {
      return
    }

    // fetch.js already redirects to login on 401. The rejected AuthenticationError is
    // expected session-expiry (missing/expired cookie), not an app bug — callers without
    // .catch() were flooding Mixpanel Page Errors across many endpoints.
    if (reason instanceof AuthenticationError && reason.status === 401) {
      return
    }

    let message: string
    let stack: string | undefined

    if (reason instanceof Error) {
      message = reason.message || 'Unhandled promise rejection'
      stack = reason.stack
    } else if (typeof reason === 'string') {
      message = reason
    } else {
      message = 'Unhandled promise rejection'
      try {
        message = JSON.stringify(reason).substring(0, 200)
      } catch {
        // Keep default message
      }
    }

    trackErrorToMixpanel('unhandled_rejection', message, undefined, undefined, undefined, stack)
  })
}

/**
 * Use this for errors that are caught and handled (e.g., in Error Boundaries, try/catch blocks).
 * @param error - The error object to track
 * @param context - Optional additional context (e.g., component_stack, action, etc.)
 *
 * @example
 * // In an Error Boundary
 * trackError(error, { error_type: 'react_boundary_error', component_stack: info.componentStack })
 * // In a try/catch block
 * try {
 *   await submitForm(data)
 * } catch (error) {
 *   trackError(error, { action: 'form_submit' })
 *   showErrorToast('Failed to save')
 * }
 */
export function trackError(error: Error | unknown, context?: Record<string, unknown>) {
  // Extract error details
  let message = 'Unknown error'
  let stack: string | undefined

  if (error instanceof Error) {
    message = error.message || 'Unknown error'
    stack = error.stack
  } else if (typeof error === 'string') {
    message = error
  } else {
    try {
      message = JSON.stringify(error).substring(0, 200)
    } catch {
      message = 'Unknown error'
    }
  }

  // Initialize deduplication set if needed
  if (!window.__mixpanelTrackedErrors) {
    window.__mixpanelTrackedErrors = new Set()
  }

  // Deduplicate errors (same error type + message within session)
  const errorType = (context?.error_type as string) || 'handled_error'
  const errorKey = getErrorKey(errorType, message)
  if (window.__mixpanelTrackedErrors.has(errorKey)) {
    return // Skip duplicate error
  }
  window.__mixpanelTrackedErrors.add(errorKey)

  // Build consistent event properties (same schema for all error types)
  track(MIXPANEL_EVENT.PAGE_ERROR, {
    event_type: 'page_error',
    error_type: errorType,
    error_subtype: context?.error_subtype || null,
    error_message: message.substring(0, 500),
    error_source: null,
    error_line: null,
    error_column: null,
    error_stack: stack ? stack.substring(0, 1000) : null,
    component_stack: context?.component_stack ? String(context.component_stack).substring(0, 1000) : null,
    trace_id: null
  })
}

/**
 * A internal wrapper to run common checks and add common props to
 *  all Mixpanel tracking events.
 */
export function track(
  event_type: MIXPANEL_EVENT | string,
  props: Record<string, string | number | null | undefined | object>
) {
  // Early exit if Mixpanel is disabled
  if (window.__mixpanelEnabled === false) {
    return
  }

  // Redact search parameter value to avoid sending user input while keeping other params
  const sanitizedUrl = redactSearchParam(window.location.href)

  // Add common properties to all events
  const objToLog = {
    page: window.location.pathname,
    $current_url: sanitizedUrl,
    domain: window.location.hostname,
    browser_language: navigator.language,
    title: document.title,
    ...props
  }

  // Local dev mode: log to console instead of sending to Mixpanel
  if (window.__mixpanelLocalMode) {
    // eslint-disable-next-line no-console
    console.log(`[Mixpanel Local] ${event_type}`, objToLog)
    return
  }

  const mixpanelInstance = window.mixpanel || mixpanel
  if (!mixpanelInstance || typeof mixpanelInstance.track !== 'function') {
    return
  }

  // Safety check: Ensure Mixpanel is fully initialized with super properties
  // If not initialized, skip tracking to avoid sending incomplete data
  if (!isMixpanelReady()) {
    return
  }

  try {
    mixpanelInstance.track(event_type, objToLog)
  } catch {
    // Silently fail - don't let error tracking cause more errors
  }
}

// ============================================================================
// MIXPANEL READY CHECK
// ============================================================================

/**
 * Check if Mixpanel is fully initialized (not just the stub)
 * The stub queues commands, but we want the real library loaded
 */
function isMixpanelReady(): boolean {
  const mp = window.mixpanel || mixpanel
  // Check if persistence exists - this is only available after full library loads

  return !!(mp && (mp as any).persistence)
}

/**
 * Wait for Mixpanel to be ready, then execute callback
 * Checks immediately, if not ready waits 100ms
 */
function whenMixpanelReady(callback: () => void): void {
  if (isMixpanelReady()) {
    // Already ready, execute immediately
    callback()
  } else {
    // Not ready yet, wait 100ms (plenty of time for initialization)
    setTimeout(callback, 100)
  }
}

// ============================================================================
// URL UTILITIES
// ============================================================================

// Utility to get the base path from a URL (e.g., https://domain.com/invoices/builder/4 -> https://domain.com/invoices)
function getBasePath(url: string): string {
  try {
    const { origin, pathname } = new URL(url)
    const segments = pathname.split('/').filter(Boolean)
    const basePath = segments.length > 0 ? `/${segments[0]}` : '/'
    return `${origin}${basePath}`
  } catch {
    // If url is relative or invalid, fallback to just the first segment
    const segments = url.split('/').filter(Boolean)
    return segments.length > 0 ? `/${segments[0]}` : '/'
  }
}

/**
 * Lightweight function to track Mixpanel page views on Next.js route changes.
 * This function only tracks - it does NOT make any API calls or initialize Mixpanel.
 * Use this on route changes instead of addMixpanelAnalyticsToPage() to avoid unnecessary overhead.
 *
 * The function uses a cached settings-gate value to avoid API calls on every route change.
 *
 * Note: Super properties (user_id, tenant_id, role, rbac_role, environment_type,
 * tags_owner, tags_purpose) are automatically included in every event via
 * mixpanel.register() during initialization.
 */
export function trackMixpanelPageView() {
  const referrerBase = getBasePath(document.referrer)
  const eventName = getPageViewEventName(window.location.href)
  const loadTime = getPageLoadTime()

  // Note: Super properties are automatically included from mixpanel.register()
  // and don't need to be passed here
  track(eventName, {
    event_type: 'page_view',
    $referrer: referrerBase,
    loadTime
  })
}

// Function to initialize and configure Mixpanel analytics
const addMixpanelAnalytics = (response: AnalyticsApiResponse) => {
  // Check if Mixpanel was already initialized by Django template
  if (window.mixpanel && window.mixpanel.__loaded) {
    return
  }

  try {
    const MIXPANEL_PROJECT_TOKEN = response.mixpanel_analytics_metadata.token
    const userId = response.mixpanel_analytics_metadata.mixpanel_user_id

    // Initialize Mixpanel
    mixpanel.init(MIXPANEL_PROJECT_TOKEN, {
      debug: false,
      track_pageview: false,
      api_host: 'https://wa.workday.com/api',
      persistence: 'localStorage'
    })

    // Identify the user
    mixpanel.identify(userId)

    // Set user properties
    mixpanel.people.set({
      id: userId,
      role: response.role,
      rbac_role: response.rbac_role,
      user_locale: response.user_locale,
      user_timezone: response.user_timezone,
      domain: response.domain,
      environment_type: response.environment_type,
      tags_owner: response.tags_owner,
      tags_purpose: response.tags_purpose,
      tenant_id: response.tenant,
      tenant_locale: response.tenant_locale,
      tenant_timezone: response.tenant_timezone
    })

    // Register super properties (automatically included in every event)
    mixpanel.register({
      user_id: userId,
      tenant_id: response.tenant,
      role: response.role,
      rbac_role: response.rbac_role,
      environment_type: response.environment_type,
      tags_owner: response.tags_owner,
      tags_purpose: response.tags_purpose
    })

    // Wait for Mixpanel to be fully ready before tracking initial page view
    whenMixpanelReady(() => {
      trackMixpanelPageView()
    })

    // Track page views on browser back/forward navigation
    window.onpopstate = trackMixpanelPageView

    // Initialize error tracking
    initMixpanelErrorTracking()
  } catch (error) {
    console.error('Mixpanel initialization failed:', error)
  }
}

// Function to add Mixpanel analytics to the page
export function addMixpanelAnalyticsToPage() {
  analyticsApi
    .config()
    .then((response: AnalyticsApiResponse) => {
      // Check if Mixpanel is enabled via settings
      const isMixpanelEnabled = response.mixpanel_analytics_metadata.enabled

      // Cache the enabled status globally for use in trackMixpanelPageView
      window.__mixpanelEnabled = isMixpanelEnabled

      if (isMixpanelEnabled) {
        if (['production', 'qa'].includes(response.domain)) {
          addMixpanelAnalytics(response)
        } else if (response.domain.includes('local')) {
          // Local dev mode: log events to console instead of Mixpanel
          window.__mixpanelLocalMode = true
          // eslint-disable-next-line no-console
          console.log('[Mixpanel Local] Local dev mode enabled — events will be logged to console')
          trackMixpanelPageView()
          initMixpanelErrorTracking()
        }
      }
    })
    .catch(error => {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch analytics config:', error)
      // On error, assume disabled and cache the result
      window.__mixpanelEnabled = false
    })
}

if (typeof window !== 'undefined') {
  window.addMixpanelAnalyticsToPage = addMixpanelAnalyticsToPage
  // Expose track globally for legacy jQuery code
  ;(window as any).trackMixpanel = track
}
